/* eslint-disable no-console */
import React, { FC, Suspense, useEffect, useState } from 'react';
import { Modal } from '@mui/material';
import { isEmpty } from 'lodash';

import {
  CustomModal,
  FileDropZone,
  ImageCropper,
  ImagePreviewCard
} from '@components';
import {
  CROP_UNSUPPORTED_FORMATS,
  FILE_UPLOAD_STATUS,
  MAX_DOCUMENT_UPLOAD_FILE_SIZE_MB,
  SUPPORTED_FILE_FORMATS
} from '@constants/common';
import { translate } from '@utils/locale';
import {
  DeleteIcon,
  FileUploaderIcon,
  RefreshIcon,
  SuccessIcon,
  UploadFailedIcon
} from '@assets/icons';
import { convertStringToSnakeCase } from '@utils/common';
import WarningIcon from '@mui/icons-material/Warning';

import {
  useGeneratePreSignedURLMutation,
  useUploadFileToS3Mutation
} from '../api';
import { FileUploaderPropsType } from '../types';

const FileUploader: FC<FileUploaderPropsType> = props => {
  const {
    name,
    enableCrop,
    sizeInMb,
    supportedFormats = SUPPORTED_FILE_FORMATS,
    fileUploadSuccessHandler,
    deleteFileHandler,
    errors,
    previousFile,
    aspectRatio,
    showPreview = false,
    isDisabled = false,
    imageInfo,
    fileUploadFailureHandler,
    resetUploadedFile,
    retryUpload,
    customUploaderStyle,
    hideUploadFileIcon = false,
    customUploadText,
    description,
    showConfirmationModal = false,
    fileUploadedUrl = ''
  } = props;

  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [isSizeConfirmationModalOpen, setIsSizeConfirmationModalOpen] =
    useState<boolean>(false);
  const [status, setStatus] = useState<string>('');

  const [generatePreSignedURL] = useGeneratePreSignedURLMutation();
  const [uploadFileToS3] = useUploadFileToS3Mutation();

  const handleFileUpload = (file: File) => {
    setUploadedFile(file);
    if (
      !enableCrop ||
      Object.values(CROP_UNSUPPORTED_FORMATS).includes(file.type)
    )
      handleUpload(file);
    else setIsModalOpen(true);
  };

  const bytesToMB = (bytes: number = 0) => {
    const megabytes = bytes / 1000000;
    return megabytes.toFixed(2);
  };

  const handleRemoveFileClick = () => {
    setUploadedFile(null);
    setStatus('');
    deleteFileHandler();
  };

  const handleUpload = async (file: File) => {
    setIsModalOpen(false);
    if (file.size > sizeInMb * 1000000) {
      if (
        showConfirmationModal &&
        file.size <= MAX_DOCUMENT_UPLOAD_FILE_SIZE_MB * 1000000
      ) {
        setIsSizeConfirmationModalOpen(true);
        return;
      }
      setStatus(FILE_UPLOAD_STATUS.failed);
      return;
    }
    uploadFile(file);
  };

  const uploadFile = async (file: File) => {
    setUploadedFile(file);
    setStatus(FILE_UPLOAD_STATUS.uploading);
    const payload = {
      content_type: file.type,
      content_name: convertStringToSnakeCase(file.name)
    };
    try {
      const response = await generatePreSignedURL(payload).unwrap();
      if (response && response.result.upload_url) {
        const uploadFileToS3Request = uploadFileToS3({
          url: response.result.upload_url,
          payload: file,
          contentType: file.type
        });
        await uploadFileToS3Request.unwrap();
        setStatus(FILE_UPLOAD_STATUS.success);
        setIsSizeConfirmationModalOpen(false);
        fileUploadSuccessHandler(file, response.result.path);
      }
    } catch (error) {
      setStatus(FILE_UPLOAD_STATUS.failed);
      if (fileUploadFailureHandler) fileUploadFailureHandler(file);
      throw error;
    }
  };

  const onClickCropModalCloseButton = () => {
    setIsModalOpen(false);
  };

  const renderContent = (hasError: boolean = false) => (
    <div
      className={`flex justify-between items-center 
      ${
        showPreview && status === FILE_UPLOAD_STATUS.success
          ? ''
          : 'p-5 rounded-lg border  border-solid bg-white '
      } w- min-h-[53px] 
  
        ${
          hasError && status === FILE_UPLOAD_STATUS.failed
            ? 'border-red'
            : 'border-gray-200'
        }`}>
      {status === FILE_UPLOAD_STATUS.uploading ||
      (status === FILE_UPLOAD_STATUS.success && !showPreview) ? (
        <div
          className={`flex  grow items-center space-x-4 w-full   leading-7 ${customUploaderStyle}`}>
          {!hideUploadFileIcon ? (
            <a
              href={fileUploadedUrl}
              download
              target="_blank"
              rel="noopener noreferrer">
              <FileUploaderIcon className="shrink-0 m-4 " />
            </a>
          ) : (
            <></>
          )}
          <div className="flex flex-col mr-2  ">
            <div className="overflow-hidden  w-[149px] text-[16px] font-normal text-black/80 text-ellipsis whitespace-nowrap">
              {uploadedFile?.name}
            </div>
            <div className="flex flex-row space-x-2 text-[14px] text-black/60">
              <span>{`${bytesToMB(uploadedFile?.size)}MB`}</span>
              <span>
                {status === FILE_UPLOAD_STATUS.success
                  ? translate('fileUpload.complete')
                  : translate('fileUpload.loading')}
              </span>
            </div>
          </div>

          <div className="flex grow">
            {!isDisabled && (
              <DeleteIcon
                className="shrink-0 m-4 cursor-pointer delete-icon"
                onClick={handleRemoveFileClick}
              />
            )}
            {status === FILE_UPLOAD_STATUS.success ? (
              <SuccessIcon className="mt-4 w-5 h-5" />
            ) : (
              <RefreshIcon className="mt-4 w-5 h-5  animate-rotate" />
            )}
          </div>
        </div>
      ) : (
        <>
          {FILE_UPLOAD_STATUS.success === status &&
          showPreview &&
          uploadedFile ? (
            <div className="w-full ">
              <ImagePreviewCard
                name={name}
                isEdit={true}
                handleDeleteClick={handleRemoveFileClick}
                imageUrl={URL.createObjectURL(uploadedFile)}
                fileName={uploadedFile?.name}
                fileSize={Number(bytesToMB(uploadedFile?.size))}
              />
            </div>
          ) : (
            <div className="flex flex-row grow items-center space-x-4 w-full leading-7 text-red">
              <UploadFailedIcon className="m-4" />
              <div className="flex flex-col mr-2">
                <span className="text-[16px]">
                  {translate('fileUpload.uploadFailed')}
                </span>
                <div className="flex flex-row space-x-2 text-[14px]">
                  <span>{translate('fileUpload.fileTooLarge')}</span>
                  <span>{translate('fileUpload.failed')}</span>
                </div>
              </div>
              <div className="flex grow " />
              <div className="ml-auto cursor-pointer ">
                <DeleteIcon
                  className="delete-icon"
                  onClick={handleRemoveFileClick}
                />
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );

  const handleCancelClick = () => {
    setIsSizeConfirmationModalOpen(false);
  };

  const getModalBody = () => {
    return (
      <div className="mb-4">
        <div className="flex flex-col items-center p-2 text-[13px] bg-gray-100 rounded-sm border">
          <div className="max-w-xs text-center truncate">
            {uploadedFile?.name}
          </div>
          <div>
            {translate('largeFile.fileSize')}&nbsp;
            <span className="font-semibold text-red">
              {bytesToMB(uploadedFile?.size)}
              {translate('largeFile.mb')}
            </span>
          </div>
          <div>
            {translate('largeFile.sizeLimit')}&nbsp;
            {sizeInMb}
            {translate('largeFile.mb')}
          </div>
        </div>
        <div className="flex flex-col gap-2 items-center mt-4 text-[14px]">
          <div>
            {translate('largeFile.description', {
              sizeInMb: sizeInMb
            })}
          </div>
          <div>{translate('largeFile.continueMessage')}</div>
        </div>
      </div>
    );
  };

  useEffect(() => {
    if (resetUploadedFile) handleRemoveFileClick();
  }, [resetUploadedFile]);

  useEffect(() => {
    if (previousFile) {
      setUploadedFile(previousFile);
      setStatus(FILE_UPLOAD_STATUS.success);
    }
  }, [previousFile]);

  useEffect(() => {
    if (retryUpload) {
      let fileN = uploadedFile;
      handleRemoveFileClick();
      handleFileUpload(fileN as File);
    }
  }, [retryUpload]);

  return (
    <>
      {uploadedFile && (
        <Modal open={enableCrop && isModalOpen} onClose={handleRemoveFileClick}>
          <div className="w-full h-full bg-black/60">
            <div className="flex justify-center items-center h-full">
              <Suspense fallback={<></>}>
                <div
                  className="flex flex-col justify-between p-4  w-[40%] min-w-[360px] max-w-3xl 
                !min-h-[200px] !max-h-[400px] 
                 bg-white rounded-lg shadow-lg lg:!min-h-[500px]  lg:!max-h-[500px] xl:!min-h-[700px] 
              xl:!max-h-[700px] 2xl:!min-h-[850px] 2xl:!max-h-[850px]">
                  <ImageCropper
                    src={URL.createObjectURL(uploadedFile)}
                    file={{
                      name: uploadedFile.name,
                      type: uploadedFile.type,
                      size: uploadedFile.size
                    }}
                    handleCropComplete={handleUpload}
                    aspectRatio={aspectRatio}
                    imageInfo={imageInfo}
                    handleClose={onClickCropModalCloseButton}
                  />
                </div>
              </Suspense>
            </div>
          </div>
        </Modal>
      )}
      {status ? (
        renderContent(
          !isEmpty(errors) && !!(errors[name] as { message?: string })?.message
        )
      ) : (
        <FileDropZone
          supportedFormats={supportedFormats}
          onFileUpload={handleFileUpload}
          maxSizeInMb={sizeInMb}
          customUploaderStyle={customUploaderStyle}
          hasError={
            !isEmpty(errors) &&
            !!(errors[name] as { message?: string })?.message
          }
          isDisabled={isDisabled}
          hideUploadFileIcon={hideUploadFileIcon}
          customUploadText={customUploadText}
        />
      )}
      {!isEmpty(errors) && (errors[name] as { message?: string })?.message ? (
        <p className="mx-[14px] mt-[3px] text-xs leading-5 text-red">
          {(errors[name] as { message?: string })?.message}
        </p>
      ) : (
        description && (
          // eslint-disable-next-line tailwindcss/no-custom-classname
          <p
            className={`mx-[14px] mt-[3px] text-xs leading-5 text-black ${
              isDisabled ? 'text-opacity-40' : 'text-opacity-60'
            }`}>
            {description}
          </p>
        )
      )}
      <CustomModal
        isModalOpen={isSizeConfirmationModalOpen}
        header={
          <div className="flex flex-col gap-2 items-center">
            <WarningIcon color="warning" fontSize="large" />
            <span>{translate('largeFile.title')}</span>
          </div>
        }
        body={getModalBody()}
        primaryButtonLabel={translate('largeFile.primaryButtonLabel')}
        secondaryButtonVariant="text"
        secondaryButtonLabel={translate('modals.cancelButtonTitle')}
        onChangePrimaryButton={() => uploadFile(uploadedFile as File)}
        onChangeSecondaryButton={handleCancelClick}
      />
    </>
  );
};

export default FileUploader;
